import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { LANGS, ARTIST_TYPES, COMMITMENT_OPTIONS, T } from "../data/castingI18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  stage_name: "",
  full_name: "",
  email: "",
  phone: "",
  city: "",
  age: "",
  artist_type: "",
  best_link: "",
  additional_link: "",
  instagram: "",
  tiktok: "",
  youtube: "",
  spotify: "",
  motivation: "",
  feedback: "",
  commitment: "",
};

// text-base = 16px: prevents iOS auto-zoom on input focus
const inputCls =
  "w-full mt-1.5 bg-white/5 border border-white/10 rounded-md px-4 py-3 text-base text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:bg-white/[0.07] transition-colors duration-300";

const Field = ({ label, optional, tOptional, children, testid }) => (
  <div data-testid={testid}>
    <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2">
      {label}
      {optional && (
        <span className="text-[10px] tracking-[0.1em] text-secondary/80 border border-secondary/30 rounded-full px-2 py-0.5">
          {tOptional}
        </span>
      )}
    </label>
    {children}
  </div>
);

/**
 * Shared casting application experience.
 * variant="modal": rendered inside the desktop modal shell (internal scroll container).
 * variant="page": rendered as the /apply full-page experience (native document scrolling).
 */
export const CastingFlow = ({ variant = "modal", onClose, onRegisterAttemptClose }) => {
  const isPage = variant === "page";
  const [phase, setPhase] = useState("language"); // language | form | success
  const [step, setStep] = useState(1);
  const [lang, setLang] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const contentRef = useRef(null);

  const t = T[lang || "en"];
  const dirty = useMemo(
    () => Object.values(form).some((v) => String(v).trim() !== ""),
    [form]
  );

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const scrollToTop = () => {
    if (isPage) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const hardClose = () => {
    setConfirmClose(false);
    setDuplicate(false);
    setPhase("language");
    setStep(1);
    setLang(null);
    setForm(EMPTY_FORM);
    setLoading(false);
    onClose();
  };

  const attemptClose = () => {
    if (phase === "form" && dirty) {
      setConfirmClose(true);
    } else {
      hardClose();
    }
  };

  const attemptCloseRef = useRef(attemptClose);
  attemptCloseRef.current = attemptClose;

  // Let the modal shell trigger the guarded close (backdrop click)
  useEffect(() => {
    onRegisterAttemptClose?.(() => attemptCloseRef.current());
  }, [onRegisterAttemptClose]);

  // ESC to close (guarded) — modal variant only; the page variant uses browser back / close button
  useEffect(() => {
    if (isPage) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        attemptCloseRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPage]);

  const pickLanguage = (code) => {
    setLang(code);
    setPhase("form");
    setStep(1);
    scrollToTop();
  };

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const nextStep = () => {
    if (step === 1) {
      if (
        !form.stage_name.trim() ||
        !form.full_name.trim() ||
        !form.city.trim() ||
        !form.age.trim() ||
        !form.artist_type
      ) {
        toast.error(t.errRequired);
        return;
      }
      if (!validEmail(form.email.trim())) {
        toast.error(t.errEmail);
        return;
      }
    }
    if (step === 2 && !form.best_link.trim()) {
      toast.error(t.errLink);
      return;
    }
    setStep((s) => Math.min(3, s + 1));
    scrollToTop();
  };

  const submit = async () => {
    if (!form.motivation.trim() || !form.feedback.trim() || !form.commitment) {
      toast.error(t.errRequired);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/casting`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, language: lang }),
      });
      if (res.status === 409) {
        setDuplicate(true);
        scrollToTop();
        return;
      }
      if (!res.ok) throw new Error("failed");
      setPhase("success");
      scrollToTop();
    } catch (err) {
      toast.error(t.errSubmit);
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = [t.step1, t.step2, t.step3];

  return (
    <>
      {/* Top bar */}
      <div
        className={`shrink-0 flex items-center justify-between px-6 md:px-10 pt-5 md:pt-6 pb-4 border-b border-white/5 ${
          isPage
            ? "sticky top-0 z-20 bg-[#0a0a0a]/90 backdrop-blur-xl pt-[max(1.25rem,env(safe-area-inset-top))]"
            : ""
        }`}
      >
        <p className="text-[11px] md:text-xs uppercase tracking-[0.3em] text-secondary">
          {t.discovery}
        </p>
        <div className="flex items-center gap-4">
          {phase === "form" && (
            <div
              data-testid="casting-lang-switcher"
              className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em]"
            >
              {LANGS.map((l, i) => (
                <span key={l.code} className="flex items-center gap-2">
                  {i > 0 && <span className="text-white/20">|</span>}
                  <button
                    data-testid={`casting-lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`transition-colors duration-200 ${
                      lang === l.code
                        ? "text-primary font-semibold"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {l.code.toUpperCase()}
                  </button>
                </span>
              ))}
            </div>
          )}
          <button
            data-testid="casting-close"
            onClick={attemptClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary transition-colors duration-300"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Content: modal = internal scroll container / page = normal document flow */}
      <div
        ref={contentRef}
        data-testid="casting-content"
        {...(isPage ? {} : { "data-lenis-prevent": true })}
        className={
          isPage
            ? "flex-1 px-6 md:px-10 pt-8 md:pt-10 pb-10 max-w-2xl w-full mx-auto"
            : "flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch] px-6 md:px-10 pt-8 md:pt-10 pb-[calc(6rem+env(safe-area-inset-bottom))] md:pb-10"
        }
      >
        <AnimatePresence mode="wait">
          {phase === "language" && (
            <motion.div
              key="language"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              className="min-h-full flex flex-col justify-center"
              data-testid="casting-language-screen"
            >
              <h2
                data-testid="casting-language-title"
                className="font-display font-bold tracking-tighter leading-[0.95] text-3xl sm:text-4xl md:text-5xl"
                style={{ wordSpacing: "0.18em" }}
              >
                {t.langTitle}
              </h2>
              <p className="mt-4 text-base md:text-lg font-light text-muted-foreground max-w-md">
                {t.langSub}
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`casting-langpick-${l.code}`}
                    onClick={() => pickLanguage(l.code)}
                    className="group flex items-center justify-between border border-white/10 rounded-xl px-5 py-4 text-left hover:border-primary hover:bg-primary/5 transition-colors duration-300"
                  >
                    <span className="flex items-center gap-3 text-sm md:text-base font-medium uppercase tracking-[0.15em]">
                      <img
                        src={l.flag}
                        alt=""
                        className="w-6 h-auto rounded-[3px] border border-white/10 shrink-0"
                      />
                      {l.label}
                    </span>
                    <ArrowRight
                      size={16}
                      className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300"
                    />
                  </button>
                ))}
              </div>
              <p className="mt-8 font-display text-sm md:text-base font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {t.langFooter}
              </p>
            </motion.div>
          )}

          {phase === "form" && (
            <motion.div
              key={`form-${step}`}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Progress */}
              <div
                data-testid="casting-progress"
                className="flex items-center gap-3 mb-8"
              >
                {[1, 2, 3].map((n) => (
                  <span key={n} className="flex items-center gap-3">
                    <span
                      data-testid={`casting-progress-${n}`}
                      className={`font-display text-sm font-bold tracking-[0.2em] transition-colors duration-300 ${
                        n === step
                          ? "text-primary"
                          : n < step
                          ? "text-foreground"
                          : "text-muted-foreground/40"
                      }`}
                    >
                      0{n}
                    </span>
                    {n < 3 && (
                      <span
                        className={`h-px w-8 md:w-12 transition-colors duration-300 ${
                          n < step ? "bg-primary" : "bg-white/15"
                        }`}
                      />
                    )}
                  </span>
                ))}
              </div>

              <h3 className="font-display font-bold tracking-tight text-2xl md:text-3xl mb-8">
                {stepTitles[step - 1]}
              </h3>

              {duplicate && (
                <div
                  data-testid="casting-duplicate-notice"
                  className="mb-8 border border-primary/50 bg-primary/10 rounded-xl p-5"
                >
                  <p className="font-display font-bold text-base md:text-lg tracking-tight text-primary">
                    {t.dupTitle}
                  </p>
                  <p className="mt-2 text-sm font-light text-muted-foreground">
                    {t.dupBody}
                  </p>
                </div>
              )}

              {step === 1 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="casting-step-1">
                  <Field label={t.stageName} testid="casting-field-stagename">
                    <input
                      data-testid="casting-input-stagename"
                      value={form.stage_name}
                      onChange={set("stage_name")}
                      className={inputCls}
                    />
                  </Field>
                  <Field label={t.fullName} testid="casting-field-fullname">
                    <input
                      data-testid="casting-input-fullname"
                      value={form.full_name}
                      onChange={set("full_name")}
                      className={inputCls}
                    />
                  </Field>
                  <Field label={t.email} testid="casting-field-email">
                    <input
                      data-testid="casting-input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => {
                        setDuplicate(false);
                        set("email")(e);
                      }}
                      className={inputCls}
                    />
                  </Field>
                  <Field label={t.phone} optional tOptional={t.optional} testid="casting-field-phone">
                    <input
                      data-testid="casting-input-phone"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      className={inputCls}
                    />
                  </Field>
                  <Field label={t.city} testid="casting-field-city">
                    <input
                      data-testid="casting-input-city"
                      value={form.city}
                      onChange={set("city")}
                      className={inputCls}
                    />
                  </Field>
                  <Field label={t.age} testid="casting-field-age">
                    <input
                      data-testid="casting-input-age"
                      type="number"
                      min="1"
                      value={form.age}
                      onChange={set("age")}
                      className={inputCls}
                    />
                  </Field>
                  <div className="sm:col-span-2" data-testid="casting-field-artisttype">
                    <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {t.artistType}
                    </label>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {ARTIST_TYPES[lang].map((type, i) => (
                        <button
                          key={type}
                          type="button"
                          data-testid={`casting-artist-type-${i}`}
                          onClick={() => set("artist_type")(type)}
                          className={`px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] border transition-colors duration-300 ${
                            form.artist_type === type
                              ? "bg-primary border-primary text-white"
                              : "border-white/15 text-muted-foreground hover:border-white/40 hover:text-foreground"
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6" data-testid="casting-step-2">
                  <div data-testid="casting-field-bestlink">
                    <label className="text-xs uppercase tracking-[0.18em] text-foreground font-semibold">
                      {t.bestLink}
                    </label>
                    <p className="mt-1.5 text-sm font-light text-muted-foreground">
                      {t.bestLinkHint}
                    </p>
                    <input
                      data-testid="casting-input-bestlink"
                      type="url"
                      inputMode="url"
                      placeholder="https://"
                      value={form.best_link}
                      onChange={set("best_link")}
                      className={inputCls}
                    />
                  </div>
                  <Field label={t.additionalLink} optional tOptional={t.optional} testid="casting-field-additionallink">
                    <input
                      data-testid="casting-input-additionallink"
                      type="url"
                      inputMode="url"
                      placeholder="https://"
                      value={form.additional_link}
                      onChange={set("additional_link")}
                      className={inputCls}
                    />
                  </Field>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4 flex items-center gap-2">
                      {t.socialTitle}
                      <span className="text-[10px] tracking-[0.1em] text-secondary/80 border border-secondary/30 rounded-full px-2 py-0.5">
                        {t.optional}
                      </span>
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        ["instagram", "Instagram"],
                        ["tiktok", "TikTok"],
                        ["youtube", "YouTube"],
                        ["spotify", "Spotify / Apple Music"],
                      ].map(([key, label]) => (
                        <input
                          key={key}
                          data-testid={`casting-input-${key}`}
                          type="url"
                          inputMode="url"
                          placeholder={label}
                          value={form[key]}
                          onChange={set(key)}
                          className={`${inputCls} mt-0`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-7" data-testid="casting-step-3">
                  <div data-testid="casting-field-motivation">
                    <label className="text-sm uppercase tracking-[0.15em] font-semibold leading-relaxed">
                      {t.q1}
                    </label>
                    <p className="mt-1 text-xs text-muted-foreground">{t.shortAnswer}</p>
                    <textarea
                      data-testid="casting-input-motivation"
                      rows={3}
                      value={form.motivation}
                      onChange={set("motivation")}
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <div data-testid="casting-field-feedback">
                    <label className="text-sm uppercase tracking-[0.15em] font-semibold leading-relaxed">
                      {t.q2}
                    </label>
                    <p className="mt-1 text-xs text-muted-foreground">{t.shortAnswer}</p>
                    <textarea
                      data-testid="casting-input-feedback"
                      rows={3}
                      value={form.feedback}
                      onChange={set("feedback")}
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <div data-testid="casting-field-commitment">
                    <label className="text-sm uppercase tracking-[0.15em] font-semibold leading-relaxed">
                      {t.q3}
                    </label>
                    <div className="mt-3 space-y-2">
                      {COMMITMENT_OPTIONS[lang].map((opt, i) => (
                        <button
                          key={opt}
                          type="button"
                          data-testid={`casting-commitment-${i}`}
                          onClick={() => set("commitment")(opt)}
                          className={`w-full text-left px-5 py-3.5 rounded-xl border text-xs md:text-sm uppercase tracking-[0.12em] transition-colors duration-300 ${
                            form.commitment === opt
                              ? "bg-primary/10 border-primary text-foreground"
                              : "border-white/10 text-muted-foreground hover:border-white/30 hover:text-foreground"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div
                    data-testid="casting-opportunity"
                    className="border-l-2 border-primary pl-5 py-1"
                  >
                    <p className="font-display font-bold text-base md:text-lg tracking-tight">
                      {t.oppTitle}
                    </p>
                    <p className="mt-2 text-sm font-light text-muted-foreground">
                      {t.oppBody}
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {phase === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="min-h-full flex flex-col justify-center text-center"
              data-testid="casting-success"
            >
              <CheckCircle2 className="mx-auto text-secondary mb-6" size={52} />
              <h2 className="font-display font-bold tracking-tighter text-4xl md:text-5xl">
                {t.successTitle}
              </h2>
              <p className="mt-4 text-base md:text-lg font-light text-muted-foreground">
                {t.successBody1}
              </p>
              <p className="mt-2 text-sm md:text-base font-light text-muted-foreground max-w-md mx-auto">
                {t.successBody2}
              </p>
              <p className="mt-10 font-display text-sm md:text-base font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {t.successFooter1}
                <br />
                <span className="text-primary">{t.successFooter2}</span>
              </p>
              <button
                data-testid="casting-done-btn"
                onClick={hardClose}
                className="mt-10 mx-auto inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full text-sm font-semibold uppercase tracking-[0.15em] hover:brightness-110 hover:scale-[1.03] transition-[transform,filter] duration-300"
              >
                {t.done}
                <ArrowUpRight size={18} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer nav (form phase only) */}
      {phase === "form" && (
        <div
          className={`shrink-0 flex items-center justify-between gap-4 px-6 md:px-10 pt-4 md:pt-5 pb-[max(1rem,env(safe-area-inset-bottom))] md:pb-[max(1.25rem,env(safe-area-inset-bottom))] border-t border-white/5 bg-[#0a0a0a] ${
            isPage ? "sticky bottom-0 z-20 bg-[#0a0a0a]/90 backdrop-blur-xl" : ""
          }`}
        >
          {step > 1 ? (
            <button
              data-testid="casting-back-btn"
              onClick={() => {
                setStep((s) => Math.max(1, s - 1));
                scrollToTop();
              }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              <ArrowLeft size={15} />
              {t.back}
            </button>
          ) : (
            <span />
          )}
          {step < 3 ? (
            <button
              data-testid="casting-continue-btn"
              onClick={nextStep}
              className="inline-flex items-center gap-3 bg-primary text-white px-8 py-3.5 rounded-full text-xs md:text-sm font-semibold uppercase tracking-[0.15em] hover:brightness-110 hover:scale-[1.02] transition-[transform,filter] duration-300"
            >
              {t.continue}
              <ArrowRight size={16} />
            </button>
          ) : (
            <button
              data-testid="casting-submit-btn"
              onClick={submit}
              disabled={loading}
              className="inline-flex items-center gap-3 bg-primary text-white px-8 py-3.5 rounded-full text-xs md:text-sm font-semibold uppercase tracking-[0.15em] hover:brightness-110 hover:scale-[1.02] transition-[transform,filter] duration-300 disabled:opacity-60"
            >
              {loading ? t.submitting : t.submit}
              <ArrowUpRight size={16} />
            </button>
          )}
        </div>
      )}

      {/* Dirty-close confirmation */}
      <AnimatePresence>
        {confirmClose && (
          <motion.div
            data-testid="casting-confirm-close"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`${
              isPage ? "fixed z-50" : "absolute z-10"
            } inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6`}
          >
            <div className="text-center max-w-xs">
              <p className="font-display font-bold text-xl tracking-tight">
                {t.confirmTitle}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{t.confirmBody}</p>
              <div className="mt-6 flex flex-col gap-3">
                <button
                  data-testid="casting-confirm-keep"
                  onClick={() => setConfirmClose(false)}
                  className="bg-primary text-white px-6 py-3 rounded-full text-xs font-semibold uppercase tracking-[0.15em] hover:brightness-110 transition-[filter] duration-300"
                >
                  {t.confirmKeep}
                </button>
                <button
                  data-testid="casting-confirm-discard"
                  onClick={hardClose}
                  className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-300"
                >
                  {t.confirmDiscard}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
